import { useRef, useCallback, useState } from 'react';

interface UseSwipeActionOptions {
  onSwipeRight?: () => void;
  threshold?: number;
  enabled?: boolean;
}

interface SwipeHandlers {
  onPointerDown: (e: React.PointerEvent) => void;
  onPointerMove: (e: React.PointerEvent) => void;
  onPointerUp: (e: React.PointerEvent) => void;
  onPointerCancel: (e: React.PointerEvent) => void;
}

interface UseSwipeActionReturn {
  handlers: SwipeHandlers;
  offset: number;
  isSwiping: boolean;
}

/**
 * Hook for detecting horizontal swipe gestures on an element.
 * Uses pointer events (works for both touch and mouse).
 * Preserves vertical scrolling via touch-action: pan-y on the element.
 */
export function useSwipeAction({
  onSwipeRight,
  threshold = 80,
  enabled = true,
}: UseSwipeActionOptions): UseSwipeActionReturn {
  const [offset, setOffset] = useState(0);
  const [isSwiping, setIsSwiping] = useState(false);

  const startX = useRef(0);
  const startY = useRef(0);
  const pointerId = useRef<number | null>(null);
  const directionLocked = useRef<'horizontal' | 'vertical' | null>(null);

  const reset = useCallback(() => {
    setOffset(0);
    setIsSwiping(false);
    pointerId.current = null;
    directionLocked.current = null;
  }, []);

  const onPointerDown = useCallback((e: React.PointerEvent) => {
    if (!enabled) return;
    // Only track primary pointer (single finger / left mouse)
    if (!e.isPrimary) return;

    pointerId.current = e.pointerId;
    startX.current = e.clientX;
    startY.current = e.clientY;
    directionLocked.current = null;
    setIsSwiping(false);
    setOffset(0);

    // Capture pointer so we get moves even outside the element
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
  }, [enabled]);

  const onPointerMove = useCallback((e: React.PointerEvent) => {
    if (!enabled || pointerId.current === null || e.pointerId !== pointerId.current) return;

    const dx = e.clientX - startX.current;
    const dy = e.clientY - startY.current;

    // Lock direction after 10px of movement
    if (directionLocked.current === null && (Math.abs(dx) > 10 || Math.abs(dy) > 10)) {
      directionLocked.current = Math.abs(dx) > Math.abs(dy) ? 'horizontal' : 'vertical';
    }

    // If vertical scroll, bail out
    if (directionLocked.current === 'vertical') {
      return;
    }

    // Only allow right swipe (positive dx)
    if (directionLocked.current === 'horizontal') {
      const clampedOffset = Math.max(0, Math.min(dx, threshold * 1.5));
      setOffset(clampedOffset);
      setIsSwiping(clampedOffset > 10);
    }
  }, [enabled, threshold]);

  const onPointerUp = useCallback((e: React.PointerEvent) => {
    if (!enabled || e.pointerId !== pointerId.current) return;

    if (offset >= threshold && onSwipeRight) {
      onSwipeRight();
    }

    reset();
  }, [enabled, offset, threshold, onSwipeRight, reset]);

  const onPointerCancel = useCallback((e: React.PointerEvent) => {
    if (e.pointerId !== pointerId.current) return;
    reset();
  }, [reset]);

  return {
    handlers: {
      onPointerDown,
      onPointerMove,
      onPointerUp,
      onPointerCancel,
    },
    offset,
    isSwiping,
  };
}
